<template>
  <div class="max-w-md mx-auto mt-8 bg-white p-6 rounded-xl shadow-md">
    <div class="text-center">
      <h2 class="text-2xl font-bold mb-4">Keycloak Login</h2>
      
      <div v-if="loading" class="space-y-4">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
        <p class="text-gray-600">Verifying authentication...</p>
      </div>

      <div v-if="error" class="text-red-500">
        <p class="mb-4">{{ error }}</p>
        <router-link to="/login" class="btn">
          Back to Login
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { handleOIDCCallback } from '@/api/auth';
import { useAuth } from '@/stores/auth';

const router = useRouter();
const route = useRoute();
const auth = useAuth();

const loading = ref(true);
const error = ref(null);

onMounted(async () => {
  try {
    // Extract authorization code and state from URL
    const code = route.query.code;
    const state = route.query.state;

    if (!code) {
      throw new Error('No authorization code received');
    }

    // Verify state parameter
    const storedState = sessionStorage.getItem('oidc_state');
    if (state !== storedState) {
      throw new Error('Invalid state parameter - possible CSRF attack');
    }

    // Retrieve code_verifier from sessionStorage (for PKCE)
    const codeVerifier = sessionStorage.getItem('oidc_code_verifier');
    if (!codeVerifier) {
      throw new Error('Code verifier not found - PKCE flow incomplete');
    }

    // Build redirect_uri (must match the one used in authorization request)
    const redirectUri = `${window.location.origin}/auth/callback`;

    // Exchange code for tokens via backend
    const response = await handleOIDCCallback(code, redirectUri, codeVerifier);
    const { access_token, refresh_token } = response.data;

    // Clean up session storage
    sessionStorage.removeItem('oidc_code_verifier');
    sessionStorage.removeItem('oidc_state');

    // Login with tokens (mark as OIDC login)
    auth.login(access_token, refresh_token, true);

    // Redirect to home
    router.push('/');
  } catch (err) {
    console.error('OIDC callback error:', err);
    error.value = err.response?.data?.detail || err.message || 'Authentication failed';
    loading.value = false;

    // Clean up session storage on error
    sessionStorage.removeItem('oidc_code_verifier');
    sessionStorage.removeItem('oidc_state');
  }
});
</script>

<style scoped>
.btn {
  padding: 0.5rem 1rem;
  background-color: #4f46e5;
  color: white;
  border-radius: 0.375rem;
  display: inline-block;
  text-decoration: none;
}

.btn:hover {
  background-color: #4338ca;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}
</style>
